// src/components/site-editor/panels/GalleryPanel.js
import React, { useState, useRef, useEffect } from 'react';
import { ImagePlus, Pencil, Trash2, Type, Plus } from 'lucide-react';
import { useWebsiteBuilder } from '../../../contexts/WebsiteBuilderContext';
import InlineCallout from '../InlineCallout';
import Toggle from '../../Toggle';
import { processGalleryImage } from '../../../utils/galleryImageProcessor';
import { deleteFile } from '../../../services/storage-service';

const GalleryPanel = ({ setCustomBackHandler }) => {
  const { state, actions } = useWebsiteBuilder();

  // Handle migration from old structure { images: [], layout: 'grid' }
  let gallery = state.draft.content.gallery || [];
  if (!Array.isArray(gallery)) {
    // Old structure - extract images array
    gallery = gallery.images || [];
  }

  const galleryLayout = state.draft.content.galleryLayout || state.draft.content.gallery?.layout || 'grid';
  const showGallery = state.draft.content.showGallery !== false; // Default true

  const [editingImage, setEditingImage] = useState(null); // imageId or null
  const [hoveredImage, setHoveredImage] = useState(null); // imageId or null
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [_uploading, setUploading] = useState(false);
  const [uploadErrors, setUploadErrors] = useState([]);
  const fileInputRef = useRef(null);

  // Migrate old data structure on mount
  useEffect(() => {
    const oldGallery = state.draft.content.gallery;
    if (oldGallery && !Array.isArray(oldGallery)) {
      // Old structure detected - migrate to new structure
      const migratedImages = oldGallery.images || [];
      const migratedLayout = oldGallery.layout || 'grid';

      actions.updateMultiple({
        'content.gallery': migratedImages,
        'content.galleryLayout': migratedLayout,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Override back button behavior when editing an image
  useEffect(() => {
    if (setCustomBackHandler) {
      if (editingImage) {
        // When editing, Back button returns to gallery grid
        setCustomBackHandler(() => () => setEditingImage(null));
      } else {
        // When not editing, use default behavior
        setCustomBackHandler(null);
      }
    }
    return () => {
      if (setCustomBackHandler) {
        setCustomBackHandler(null);
      }
    };
  }, [editingImage, setCustomBackHandler]);

  const handleVisibilityToggle = () => {
    actions.updateContent('showGallery', !showGallery);
  };

  const handleLayoutChange = (layout) => {
    actions.updateContent('galleryLayout', layout);
  };

  const handleFileSelect = async (files) => {
    const fileArray = Array.from(files);
    const availableSlots = 12 - gallery.length;

    if (availableSlots === 0) {
      setUploadErrors(['Gallery full — max 12 images']);
      return;
    }

    if (fileArray.length > availableSlots) {
      setUploadErrors([`Can only add ${availableSlots} more image${availableSlots === 1 ? '' : 's'} (12 max)`]);
      return;
    }

    setUploading(true);
    setUploadErrors([]);

    const results = [];
    const errors = [];

    for (const file of fileArray) {
      try {
        const processed = await processGalleryImage(file);
        results.push({
          id: `gallery_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
          imageUrl: processed.imageUrl,
          thumbnail: processed.thumbnail,
          storagePath: processed.storagePath || null,
          thumbStoragePath: processed.thumbStoragePath || null,
          caption: '',
          altText: '',
          altTextManuallySet: false,
          dimensions: processed.dimensions,
          fileSize: processed.fileSize,
          order: gallery.length + results.length,
          uploadedAt: new Date().toISOString(),
        });
      } catch (error) {
        errors.push(`${file.name}: ${error.message}`);
      }
    }

    if (results.length > 0) {
      actions.updateContent('gallery', [...gallery, ...results]);

      // Auto-enable gallery visibility if it was off
      if (!showGallery) {
        actions.updateContent('showGallery', true);
      }
    }

    setUploading(false);
    setUploadErrors(errors);

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDeleteImage = (imageId) => {
    // Look up image to retrieve Storage paths before removing
    const imageToDelete = gallery.find(img => img.id === imageId);

    if (imageToDelete?.storagePath) {
      deleteFile(imageToDelete.storagePath).catch(err =>
        console.warn('⚠️ Failed to delete gallery image from Storage:', err)
      );
    }
    if (imageToDelete?.thumbStoragePath) {
      deleteFile(imageToDelete.thumbStoragePath).catch(err =>
        console.warn('⚠️ Failed to delete thumbnail from Storage:', err)
      );
    }

    const updatedGallery = gallery
      .filter(img => img.id !== imageId)
      .map((img, index) => ({ ...img, order: index }));
    actions.updateContent('gallery', updatedGallery);
    setDeleteConfirm(null);
  };

  const handleUpdateImage = (imageId, updates) => {
    const updatedGallery = gallery.map(img =>
      img.id === imageId ? { ...img, ...updates } : img
    );
    actions.updateContent('gallery', updatedGallery);
  };

  const handleUpdateCaption = (imageId, newCaption) => {
    const image = gallery.find(i => i.id === imageId);
    const updates = { caption: newCaption };

    // Auto-update alt text if it hasn't been manually set
    if (!image.altTextManuallySet) {
      updates.altText = newCaption;
    }

    handleUpdateImage(imageId, updates);
  };

  const handleUpdateAltText = (imageId, newAltText) => {
    handleUpdateImage(imageId, { altText: newAltText, altTextManuallySet: true });
  };

  // If editing an image, show edit view
  if (editingImage) {
    const image = gallery.find(img => img.id === editingImage);
    const index = gallery.findIndex(img => img.id === editingImage);

    return (
      <div className="p-6 space-y-4">
        {/* Image Preview */}
        <img
          src={image.imageUrl}
          alt={image.altText || image.caption || 'Gallery image'}
          style={{ width: '100%', borderRadius: '8px', marginBottom: '12px' }}
        />

        {/* Caption */}
        <div>
          <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
            Caption
          </label>
          <input
            type="text"
            value={image.caption}
            onChange={(e) => handleUpdateCaption(image.id, e.target.value)}
            placeholder="e.g., My healing room in Austin"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '13px',
              boxSizing: 'border-box',
            }}
            maxLength={100}
          />
          <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
            {image.caption?.length || 0}/100
          </p>
        </div>

        {/* Alt Text */}
        <div>
          <label style={{ fontSize: '13px', fontWeight: 500, display: 'block', marginBottom: '4px' }}>
            Alt Text
          </label>
          <input
            type="text"
            value={image.altText || image.caption || `Gallery image ${index + 1}`}
            onChange={(e) => handleUpdateAltText(image.id, e.target.value)}
            placeholder="Automatically generated from caption"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '13px',
              boxSizing: 'border-box',
            }}
          />
          <p style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '2px' }}>
            Helps visually impaired visitors. Generated from your caption automatically.
          </p>
        </div>

        {/* Delete Button */}
        <button
          onClick={() => setDeleteConfirm(image.id)}
          style={{
            fontSize: '13px',
            color: '#EF4444',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Delete this image
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-1">Photo Gallery</h3>
        <p className="text-sm text-gray-600">
          Showcase your practice space, healing tools, and environment
        </p>
      </div>

      {/* Visibility Toggle */}
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium text-gray-900">
          Show Gallery on website
        </label>
        <Toggle enabled={showGallery} onChange={handleVisibilityToggle} />
      </div>

      {/* Playbook Callout */}
      <InlineCallout
        text="Choose your strongest images — a curated gallery makes a bigger impression than a large one. Show your space, your tools, and yourself at work."
        playbookTopic="building-your-presence"
      />

      {/* Layout Selection */}
      <div>
          <label className="block text-sm font-medium text-gray-900 mb-3">
            Gallery Layout
          </label>
          <div className="grid grid-cols-2 gap-3">
            {/* Grid */}
            <button
              type="button"
              onClick={() => handleLayoutChange('grid')}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                galleryLayout === 'grid'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="grid grid-cols-3 gap-1 mb-2">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="aspect-square bg-gray-300 rounded-sm" />
                ))}
              </div>
              <p className="text-xs font-medium text-gray-900">Grid</p>
              <p className="text-xs text-gray-500">Clean, even layout — like Instagram</p>
            </button>

            {/* Masonry */}
            <button
              type="button"
              onClick={() => handleLayoutChange('masonry')}
              className={`p-3 rounded-lg border-2 transition-all text-left ${
                galleryLayout === 'masonry'
                  ? 'border-purple-600 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="grid grid-cols-3 gap-1 mb-2">
                <div className="col-span-1 aspect-square bg-gray-300 rounded-sm" />
                <div className="col-span-1 aspect-[1/1.5] bg-gray-300 rounded-sm" />
                <div className="col-span-1 aspect-[1/0.7] bg-gray-300 rounded-sm" />
              </div>
              <p className="text-xs font-medium text-gray-900">Masonry</p>
              <p className="text-xs text-gray-500">Images keep their original proportions</p>
            </button>
          </div>
        </div>

      {/* Upload Area or Image Grid */}
      {gallery.length === 0 ? (
        <>
          {/* Empty State - Drag & Drop Area */}
          <div
            onClick={() => fileInputRef.current?.click()}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              border: '2px dashed #E5E7EB',
              borderRadius: '12px',
              padding: '32px 16px',
              textAlign: 'center',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = '#7c3aed';
              e.currentTarget.style.background = '#F5F3FF';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = '#E5E7EB';
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <ImagePlus size={32} color="#9CA3AF" style={{ margin: '0 auto' }} />
            <p style={{ fontSize: '14px', fontWeight: 500, color: '#374151', marginTop: '12px' }}>
              Drag photos here or click to browse
            </p>
            <p style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '4px' }}>
              Max 2MB each · Up to 12 images
            </p>
          </div>
        </>
      ) : (
        <>
          {/* Image Thumbnail Grid */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '8px',
          }}>
            {gallery.map((image, index) => (
              <div
                key={image.id}
                style={{
                  position: 'relative',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  aspectRatio: '1',
                  border: '1px solid #E5E7EB',
                  cursor: 'pointer',
                }}
                onMouseEnter={() => setHoveredImage(image.id)}
                onMouseLeave={() => setHoveredImage(null)}
              >
                <img
                  src={image.thumbnail || image.imageUrl}
                  alt={image.altText || image.caption || `Image ${index + 1}`}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                />

                {/* Order number badge */}
                <div style={{
                  position: 'absolute',
                  top: '4px',
                  left: '4px',
                  background: 'rgba(0,0,0,0.6)',
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 600,
                  padding: '2px 6px',
                  borderRadius: '4px',
                }}>
                  {index + 1}
                </div>

                {/* Hover overlay - only show when hovered */}
                {hoveredImage === image.id && (
                  <div style={{
                    position: 'absolute',
                    inset: 0,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '12px',
                  }}>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setEditingImage(image.id);
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: '1px solid rgba(255,255,255,0.4)',
                        borderRadius: '8px',
                        padding: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Edit caption"
                    >
                      <Pencil size={16} color="white" />
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setDeleteConfirm(image.id);
                      }}
                      style={{
                        background: 'rgba(255,255,255,0.2)',
                        border: '1px solid rgba(255,255,255,0.4)',
                        borderRadius: '8px',
                        padding: '8px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Delete image"
                    >
                      <Trash2 size={16} color="white" />
                    </button>
                  </div>
                )}

                {/* Caption indicator - small icon if caption exists and not hovered */}
                {image.caption && hoveredImage !== image.id && (
                  <div style={{
                    position: 'absolute',
                    bottom: '4px',
                    right: '4px',
                    background: 'rgba(0,0,0,0.6)',
                    padding: '3px 5px',
                    borderRadius: '4px',
                  }}>
                    <Type size={10} color="white" />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Image count */}
          <p style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>
            {gallery.length} of 12 images
          </p>

          {/* Add More Button */}
          {gallery.length < 12 && (
            <button
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px dashed #E5E7EB',
                borderRadius: '8px',
                background: 'transparent',
                color: '#6B7280',
                fontSize: '13px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
              }}
            >
              <Plus size={16} />
              Add more photos
            </button>
          )}
        </>
      )}

      {/* Upload Errors */}
      {uploadErrors.length > 0 && (
        <div style={{
          padding: '12px',
          background: '#FEF2F2',
          border: '1px solid #FEE2E2',
          borderRadius: '8px',
        }}>
          {uploadErrors.map((error, index) => (
            <p key={index} style={{ fontSize: '12px', color: '#991B1B', margin: 0 }}>
              {error}
            </p>
          ))}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        multiple
        style={{ display: 'none' }}
        onChange={(e) => handleFileSelect(e.target.files)}
      />

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <DeleteConfirmModal
          onConfirm={() => handleDeleteImage(deleteConfirm)}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
};

// Delete Confirmation Modal
const DeleteConfirmModal = ({ onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className="bg-white rounded-xl shadow-xl"
        style={{ maxWidth: '400px', padding: '24px' }}
      >
        <h3 className="text-gray-900 mb-2" style={{ fontSize: '16px', fontWeight: 600 }}>
          Delete this image?
        </h3>
        <p className="mb-5" style={{ fontSize: '14px', color: '#6B7280' }}>
          This cannot be undone.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            onClick={onCancel}
            className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
            style={{ background: 'none', border: 'none', padding: '8px 12px' }}
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            className="px-6 py-2.5 text-sm font-semibold text-white bg-red-600 hover:bg-red-700 rounded-lg transition-all shadow-sm hover:shadow-md"
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );
};

export default GalleryPanel;
